import { useState } from 'react';
import { useAssets, useDomains } from '@/lib/queries';
import PhotoGrid from '@/components/PhotoGrid';
import PhotoUploadSheet from '@/components/PhotoUploadSheet';
import { Button } from '@/components/ui/button';
import PullToRefresh from '@/components/PullToRefresh';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';

export default function Photos() {
  const [domainFilter, setDomainFilter] = useState('');
  const assets = useAssets({ domain: domainFilter || undefined, limit: 200 });
  const domains = useDomains();
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['assets'] });
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-8">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="space-y-2">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
              Observability · Photos
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Photos</h1>
            <p className="text-muted-foreground max-w-2xl">
              Lawn baselines, before/after shots, equipment condition, receipts. Tag liberally;
              filter later.
            </p>
          </div>
          <Button onClick={() => setUploading(true)} className="h-11 self-start sm:self-auto" data-testid="new-photo">
            <Plus className="h-4 w-4 mr-2" aria-hidden /> New photo
          </Button>
        </header>

        <div className="flex items-center gap-2 flex-wrap">
          <FilterPill label="All domains" active={domainFilter === ''} onClick={() => setDomainFilter('')} />
          {(domains.data ?? []).map((d) => (
            <FilterPill
              key={d.id}
              label={d.name}
              active={domainFilter === d.slug}
              onClick={() => setDomainFilter(d.slug)}
            />
          ))}
        </div>

        {assets.isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {assets.data ? <PhotoGrid assets={assets.data} /> : null}

        <PhotoUploadSheet open={uploading} onOpenChange={setUploading} />
      </div>
    </PullToRefresh>
  );
}

function FilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ' +
        (active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-rule text-muted-foreground hover:border-primary/30 hover:text-foreground')
      }
    >
      {label}
    </button>
  );
}
